import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, X, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useBranding } from "@/lib/branding";
import { useAuth, hasRole } from "@/lib/auth";
import { api } from "@/lib/quotationApi";

// One checklist row — button navigates unless already done.
function ChecklistItem({ it, onGo }) {
  return (
    <button
      onClick={() => !it.done && onGo(it.to)}
      disabled={it.done}
      className={`w-full flex items-center gap-3 text-left rounded-lg px-3 py-2.5 transition ${
        it.done
          ? "opacity-60 cursor-default"
          : "hover:bg-white/5 border border-white/5 hover:border-[#C5A880]/30"
      }`}
      data-testid={`onboarding-item-${it.key}`}
    >
      {it.done ? (
        <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
      ) : (
        <Circle size={18} className="text-white/30 shrink-0" />
      )}
      <div className="min-w-0">
        <div className={`text-sm font-medium ${it.done ? "line-through text-white/50" : ""}`}>
          {it.label}
        </div>
        {!it.done && <div className="text-white/40 text-xs">{it.desc}</div>}
      </div>
    </button>
  );
}

// Setup checklist definition — visibility + completion per role/state.
const buildChecklistItems = ({ role, branding, quotesCount, membersCount }) =>
  [
    {
      key: "logo",
      label: "Add your logo & brand colour",
      desc: "Make every quote look unmistakably yours.",
      done: Boolean(branding.logo_storage_path),
      to: "/admin",
      show: hasRole(role, "manager"),
    },
    {
      key: "biz",
      label: "Add business & payment details",
      desc: "GST, address, bank/UPI shown on quotes.",
      done: Boolean(branding.show_business_info || branding.show_payment_info),
      to: "/admin",
      show: hasRole(role, "manager"),
    },
    {
      key: "quote",
      label: "Create your first quotation",
      desc: "Build a tiered, shareable quote in minutes.",
      done: (quotesCount || 0) > 0,
      to: "/new",
      show: true,
    },
    {
      key: "team",
      label: "Invite your team",
      desc: "Add teammates as Manager or Staff.",
      done: (membersCount || 0) > 1,
      to: "/team",
      show: hasRole(role, "admin"),
    },
  ].filter((i) => i.show);

export default function OnboardingChecklist({ quotesCount }) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { user } = useAuth();
  const role = user?.role || "staff";
  const ws = user?.active_workspace_id || "";
  const prefKey = `onboard_dismissed_${ws}`;

  const [dismissed, setDismissed] = useState(false);
  const [membersCount, setMembersCount] = useState(null);

  // Dismissal is a server-side user preference (no browser storage).
  useEffect(() => {
    setDismissed(Boolean(user?.prefs?.[prefKey]));
  }, [user, prefKey]);

  useEffect(() => {
    if (!hasRole(role, "admin")) return;
    (async () => {
      try {
        const r = await api.get("/members");
        setMembersCount((r.data.members || []).length);
      } catch (e) {
        console.warn("Could not load members for onboarding:", e);
      }
    })();
  }, [role, ws]);

  const items = buildChecklistItems({ role, branding, quotesCount, membersCount });

  const doneCount = items.filter((i) => i.done).length;
  const allDone = doneCount === items.length;

  if (dismissed || allDone) return null;

  const dismiss = async () => {
    setDismissed(true);
    try {
      await api.post("/me/prefs", { key: prefKey, value: true });
    } catch (e) {
      console.warn("Could not persist onboarding dismissal:", e);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="card-luxe mb-8 !border-[#C5A880]/30"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#C5A880]" />
          <div>
            <div className="font-display font-semibold text-lg">Get set up</div>
            <div className="text-white/50 text-xs">
              {doneCount} of {items.length} done — finish to make the most of your workspace.
            </div>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-white/30 hover:text-white p-1"
          data-testid="onboarding-dismiss"
          aria-label="Dismiss checklist"
        >
          <X size={16} />
        </button>
      </div>

      <div className="h-1.5 rounded-full bg-white/10 mb-4 overflow-hidden">
        <div
          className="h-full bg-[#C5A880] transition-all duration-500"
          style={{ width: `${(doneCount / items.length) * 100}%` }}
        />
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <ChecklistItem key={it.key} it={it} onGo={navigate} />
        ))}
      </div>
    </motion.div>
  );
}
